import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Building2, CalendarClock, Calculator, ClipboardCheck, CreditCard, FileText, Search, Users, X } from "lucide-react";
import { practiceApi } from "../lib/practiceApi";
import "./search.css";

const GROUP_ICONS = { practices: ClipboardCheck, clients: Users, sites: Building2, estimates: Calculator, activities: CalendarClock, documents: FileText, finance: CreditCard };

const PAGES = [
  ["dashboard", "Dashboard"], ["practices", "Pratiche"], ["clients", "Clienti"], ["sites", "Cantieri"],
  ["deadlines", "Attività e scadenze"], ["coordination", "Coordinamento sicurezza"], ["documents", "Documenti"],
  ["finance", "Economia"], ["estimates", "Preventivazione"], ["settings", "Impostazioni"], ["ai", "Analisi AI"], ["audit", "Registro attività"],
];

export const isMac = () => /mac|iphone|ipad/i.test(navigator.userAgentData?.platform || navigator.platform || navigator.userAgent);
export const shortcutLabel = () => (isMac() ? "⌘ K" : "Ctrl K");

export const GlobalSearch = ({ open, onClose, onOpenTarget }) => {
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(false);
  const input = useRef(null);

  useEffect(() => { if (open) { setQuery(""); setGroups([]); setCursor(0); setTimeout(() => input.current?.focus(), 40); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    const timer = setTimeout(() => {
      practiceApi(`/search?q=${encodeURIComponent(trimmed)}`)
        .then(result => { setGroups(result.groups || []); setCursor(0); })
        .catch(() => setGroups([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open]);

  const pageMatches = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return PAGES.slice(0, 6).map(([key, label]) => ({ id: key, title: label, detail: "Vai alla sezione", target: { page: key } }));
    return PAGES.filter(([, label]) => label.toLowerCase().includes(trimmed))
      .map(([key, label]) => ({ id: key, title: label, detail: "Vai alla sezione", target: { page: key } }));
  }, [query]);

  const allGroups = useMemo(() => {
    const list = pageMatches.length ? [{ key: "pages", label: "Sezioni", items: pageMatches }] : [];
    return [...list, ...groups];
  }, [pageMatches, groups]);

  const flat = useMemo(() => allGroups.flatMap(group => group.items.map(item => ({ ...item, group: group.key }))), [allGroups]);

  const choose = useCallback(item => { onClose(); onOpenTarget(item.target); }, [onClose, onOpenTarget]);

  useEffect(() => {
    if (!open) return;
    const handler = event => {
      if (event.key === "Escape") { onClose(); return; }
      if (event.key === "ArrowDown") { event.preventDefault(); setCursor(c => Math.min(c + 1, flat.length - 1)); }
      if (event.key === "ArrowUp") { event.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
      if (event.key === "Enter" && flat[cursor]) { event.preventDefault(); choose(flat[cursor]); }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, flat, cursor, onClose, choose]);

  if (!open) return null;
  let index = -1;

  return <div className="gs-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }} data-testid="global-search-modal">
    <section className="gs-panel" role="dialog" aria-label="Ricerca globale">
      <header>
        <Search size={17} />
        <input ref={input} data-testid="global-search-input" placeholder="Cerca pratiche, clienti, cantieri, preventivi, documenti…"
               value={query} onChange={event => setQuery(event.target.value)} />
        <kbd>esc</kbd>
        <button className="icon-btn" data-testid="global-search-close" onClick={onClose}><X size={15} /></button>
      </header>

      <div className="gs-results">
        {loading && <p className="gs-hint" data-testid="global-search-loading">Ricerca in corso…</p>}
        {!loading && query.trim().length >= 2 && !groups.length && <p className="gs-hint" data-testid="global-search-empty">Nessun risultato per “{query.trim()}”. Prova con il nome del committente, il numero di pratica o una parola contenuta nei documenti.</p>}
        {allGroups.map(group => {
          const Icon = GROUP_ICONS[group.key] || Search;
          return <div className="gs-group" key={group.key}>
            <span className="gs-group-label">{group.label}</span>
            {group.items.map(item => {
              index += 1;
              const active = index === cursor;
              return <button key={`${group.key}-${item.id}`} className={active ? "gs-item active" : "gs-item"}
                             data-testid={`global-search-result-${group.key}-${item.id}`}
                             onMouseEnter={() => setCursor(index)} onClick={() => choose(item)}>
                <Icon size={15} />
                <span><strong>{item.title}</strong>{item.detail && <em>{item.detail}</em>}</span>
              </button>;
            })}
          </div>;
        })}
      </div>

      <footer>
        <span><kbd>↑</kbd><kbd>↓</kbd> naviga</span>
        <span><kbd>invio</kbd> apri</span>
        <span><kbd>{shortcutLabel()}</kbd> apri la ricerca</span>
      </footer>
    </section>
  </div>;
};
